import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewService, IMessage, IParticipant } from '../service/review.service';
import { EmojiPicker } from './emoji-picker.component';

interface ChatSidebarProps {
  slug: string;
  senderName: string;
  senderPreviousNames?: Set<string>;
  participants: IParticipant[];
  isTeamReview?: boolean;
  isAdmin?: boolean;
  adminNames?: string[];
  isOpen: boolean;
  onClose: () => void;
}

const renderMessageText = (text: string, participants: IParticipant[], adminNames?: string[]) => {
  const names = [...participants.map((p) => p.name), ...(adminNames || []), 'here', 'team', 'admin'];

  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(@(?:${escaped.join('|')}))(?=\\s|$|[.,!?])`, 'gi');

  const parts = text.split(pattern);
  return parts.map((part, i) => {
    if (pattern.test(part)) {
      pattern.lastIndex = 0;
      return (
        <span key={i} className="font-semibold px-[4px] py-[1px] rounded-[4px] bg-[#0ea5c9] text-white">
          {part}
        </span>
      );
    }
    return part;
  });
};

export const ChatSidebar = ({
  slug,
  senderName,
  senderPreviousNames,
  participants,
  isTeamReview,
  isAdmin,
  adminNames,
  isOpen,
  onClose
}: ChatSidebarProps) => {
  const isMySender = (sender: string) =>
    sender === senderName || (senderPreviousNames?.has(sender) ?? false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = React.useState('');
  const [showMentions, setShowMentions] = React.useState(false);
  const [showEmoji, setShowEmoji] = React.useState(false);
  const [mentionFilter, setMentionFilter] = React.useState('');
  const [mentionIndex, setMentionIndex] = React.useState(0);
  const [editingMessage, setEditingMessage] = React.useState<IMessage | null>(null);
  const [replyingTo, setReplyingTo] = React.useState<IMessage | null>(null);
  const [menuOpenId, setMenuOpenId] = React.useState<number | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Close menu on outside click
  React.useEffect(() => {
    if (menuOpenId === null) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpenId]);

  const { data: messages } = useQuery<IMessage[] | void>({
    queryKey: ['chat-messages', slug],
    queryFn: () => reviewService.getMessages(slug),
    refetchInterval: false,
    enabled: isOpen
  });

  const { mutate: send, isPending } = useMutation({
    mutationFn: (msg: string) => reviewService.sendMessage(slug, senderName, msg, replyingTo?.id),
    onSuccess: () => {
      setText('');
      setReplyingTo(null);
      queryClient.invalidateQueries({ queryKey: ['chat-messages', slug] });
    }
  });

  const { mutate: editMsg, isPending: editPending } = useMutation({
    mutationFn: ({ id, newText }: { id: number; newText: string }) =>
      reviewService.editMessage(slug, id, senderName, newText),
    onSuccess: () => {
      setText('');
      setEditingMessage(null);
      queryClient.invalidateQueries({ queryKey: ['chat-messages', slug] });
    },
    onError: () => {
      setEditingMessage(null);
      setText('');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (editingMessage) {
      editMsg({ id: editingMessage.id, newText: text.trim() });
    } else {
      send(text.trim());
    }
  };

  const startEdit = (msg: IMessage) => {
    setEditingMessage(msg);
    setReplyingTo(null);
    setText(msg.text);
    inputRef.current?.focus();
  };

  const startReply = (msg: IMessage) => {
    setReplyingTo(msg);
    setEditingMessage(null);
    setText('');
    inputRef.current?.focus();
  };

  const cancelAction = () => {
    setEditingMessage(null);
    setReplyingTo(null);
    setText('');
  };

  // @mention logic
  const filteredParticipants = React.useMemo(() => {
    if (!showMentions) return [];
    const filter = mentionFilter.toLowerCase();
    const people = participants
      .filter((p) => !isMySender(p.name) && p.name.toLowerCase().includes(filter))
      .map((p) => ({ id: p.id, name: p.name, type: 'person' as const }));

    const adminPeople = (adminNames || [])
      .filter((n) => !isMySender(n) && n.toLowerCase().includes(filter))
      .map((n, i) => ({ id: -(10 + i), name: n, type: 'person' as const }));

    const specials: { id: number; name: string; type: 'special' }[] = [];
    if ('here'.includes(filter)) {
      specials.push({ id: -1, name: 'here', type: 'special' });
    }
    if (isTeamReview && 'team'.includes(filter)) {
      specials.push({ id: -2, name: 'team', type: 'special' });
    }
    if ('admin'.includes(filter)) {
      specials.push({ id: -3, name: 'admin', type: 'special' });
    }
    return [...specials, ...adminPeople, ...people];
  }, [showMentions, mentionFilter, participants, senderName, isTeamReview, adminNames]);

  const insertMention = (name: string) => {
    const atIdx = text.lastIndexOf('@');
    if (atIdx >= 0) {
      const before = text.slice(0, atIdx);
      setText(`${before}@${name} `);
    }
    setShowMentions(false);
    setMentionFilter('');
    setMentionIndex(0);
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setText(val);

    const atIdx = val.lastIndexOf('@');
    if (atIdx >= 0) {
      const afterAt = val.slice(atIdx + 1);
      if ((atIdx === 0 || val[atIdx - 1] === ' ') && !afterAt.includes(' ')) {
        setShowMentions(true);
        setMentionFilter(afterAt);
        setMentionIndex(0);
        return;
      }
    }
    setShowMentions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && (editingMessage || replyingTo)) {
      cancelAction();
      return;
    }
    if (!showMentions || filteredParticipants.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((i) => Math.min(i + 1, filteredParticipants.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(filteredParticipants[mentionIndex].name);
    } else if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const messageList = (messages as IMessage[]) || [];

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed top-0 right-0 h-full z-50 bg-white border-l border-[rgba(0,0,0,0.11)] shadow-[-4px_0_20px_rgba(0,0,0,0.08)] flex flex-col transition-transform duration-300 ease-in-out w-[320px] md:w-[360px]
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between p-[16px] border-b border-[rgba(0,0,0,0.11)]">
          <h3 className="font-body text-[16px] font-semibold text-base-black">
            {t('chat.title')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-[28px] h-[28px] flex items-center justify-center rounded-full hover:bg-gray-400 transition-colors text-gray-900"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="1" y1="1" x2="13" y2="13" />
              <line x1="13" y1="1" x2="1" y2="13" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-[12px] flex flex-col gap-[8px]">
          {messageList.length === 0 && (
            <p className="text-center font-body text-[13px] text-gray-700 mt-[40px]">
              {t('chat.emptyState')}
            </p>
          )}
          {messageList.map((msg) => {
            const isMe = isMySender(msg.sender);
            return (
              <div
                key={msg.id}
                className={`group flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
              >
                <span className="font-body text-[11px] text-gray-700 mb-[2px] px-[4px]">
                  {msg.sender}
                </span>

                {/* Reply context */}
                {msg.replyTo && (
                  <div className={`max-w-[240px] px-[10px] py-[4px] mb-[2px] rounded-[8px] bg-[#F0F0F2] dark:bg-[#28282B] border-l-[2px] border-[#0ea5c9] ${isMe ? 'ml-auto' : ''}`}>
                    <p className="font-body text-[10px] font-semibold text-[#0ea5c9]">{msg.replyTo.sender}</p>
                    <p className="font-body text-[11px] text-[#818185] truncate">{msg.replyTo.text}</p>
                  </div>
                )}

                {/* Bubble row: menu + bubble, ordered by sender */}
                <div className={`flex items-center gap-[4px] ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div
                    className={`max-w-[210px] px-[12px] py-[8px] rounded-[14px] font-body text-[14px] break-words
                      ${
                        isMe
                          ? 'bg-[#1D1D1E] text-white rounded-br-[4px]'
                          : 'bg-gray-400 text-base-black rounded-bl-[4px]'
                      }
                    `}
                  >
                    {renderMessageText(msg.text, participants, adminNames)}
                  </div>

                  {/* Three-dot menu */}
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setMenuOpenId(menuOpenId === msg.id ? null : msg.id)}
                      className="text-[#BFBFC0] hover:text-[#818185] transition-colors p-[2px]"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2" />
                        <circle cx="12" cy="12" r="2" />
                        <circle cx="12" cy="19" r="2" />
                      </svg>
                    </button>
                    {menuOpenId === msg.id && (
                      <div
                        ref={menuRef}
                        className={`absolute z-10 mt-[2px] bg-white dark:bg-[#1C1C1F] rounded-[10px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_4px_20px_rgba(0,0,0,0.12)] py-[4px] min-w-[140px] animate-fadeInMenu ${
                          isMe ? 'right-0' : 'left-0'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => { startReply(msg); setMenuOpenId(null); }}
                          className="w-full flex items-center gap-[8px] px-[12px] py-[8px] font-body text-[13px] text-base-black hover:bg-[#F5F5F6] dark:hover:bg-[#28282B] transition-colors"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                          </svg>
                          Reply
                        </button>
                        {isMe && (
                          <button
                            type="button"
                            onClick={() => { startEdit(msg); setMenuOpenId(null); }}
                            className="w-full flex items-center gap-[8px] px-[12px] py-[8px] font-body text-[13px] text-base-black hover:bg-[#F5F5F6] dark:hover:bg-[#28282B] transition-colors"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Edit
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <span className="font-body text-[10px] text-gray-700 px-[4px]">
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {msg.edited && <span className="ml-[4px] italic">(edited)</span>}
                </span>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-[12px] border-t border-[rgba(0,0,0,0.11)] relative"
        >
          {/* Reply/Edit banner */}
          {(replyingTo || editingMessage) && (
            <div className="flex items-center justify-between px-[10px] py-[6px] mb-[8px] rounded-[10px] bg-[#F5F5F6] dark:bg-[#28282B] border-l-[3px] border-[#0ea5c9]">
              <div className="min-w-0 flex-1">
                <p className="font-body text-[11px] font-semibold text-[#0ea5c9]">
                  {editingMessage ? 'Editing message' : `Replying to ${replyingTo!.sender}`}
                </p>
                <p className="font-body text-[12px] text-[#818185] truncate">
                  {(editingMessage || replyingTo)!.text}
                </p>
              </div>
              <button
                type="button"
                onClick={cancelAction}
                className="text-[#BFBFC0] hover:text-[#818185] transition-colors flex-shrink-0 ml-[8px]"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="1" y1="1" x2="13" y2="13" /><line x1="13" y1="1" x2="1" y2="13" />
                </svg>
              </button>
            </div>
          )}

          {showMentions && filteredParticipants.length > 0 && (
            <div className="absolute bottom-full left-[12px] right-[12px] mb-[4px] bg-white rounded-[12px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.1)] py-[4px] max-h-[160px] overflow-y-auto">
              {filteredParticipants.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => insertMention(p.name)}
                  className={`w-full text-left px-[12px] py-[8px] font-body text-[14px] text-base-black flex items-center gap-[8px] transition-colors
                    ${i === mentionIndex ? 'bg-[#EFEFF1]' : 'hover:bg-gray-400'}
                  `}
                >
                  {p.type === 'special' ? (
                    <span className="w-[24px] h-[24px] rounded-full bg-orange-400 text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                      @
                    </span>
                  ) : (
                    <span className="w-[24px] h-[24px] rounded-full bg-[#1D1D1E] text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                      {p.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span>
                    {p.name}
                    {p.type === 'special' && (
                      <span className="text-[12px] text-[#818185] ml-[6px]">
                        {p.name === 'here' ? t('chat.notifyEveryone') : p.name === 'admin' ? t('chat.notifyAdmins') : t('chat.notifyTeam')}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
          {showEmoji && (
            <EmojiPicker
              onSelect={(emoji) => {
                setText((prev) => prev + emoji);
                inputRef.current?.focus();
              }}
              onClose={() => setShowEmoji(false)}
            />
          )}
          <div className="flex gap-[8px]">
            <button
              type="button"
              onClick={() => setShowEmoji(!showEmoji)}
              className="w-[40px] h-[40px] flex items-center justify-center rounded-full hover:bg-[#E0E0E3] transition-all flex-shrink-0 text-[18px]"
            >
              😊
            </button>
            <input
              ref={inputRef}
              value={text}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={editingMessage ? 'Edit message...' : t('chat.placeholder')}
              className="flex-1 h-[40px] px-[14px] text-[14px] font-body text-base-black bg-gray-400 border-none rounded-[20px] focus:outline-none focus:ring-[2px] focus:ring-[rgba(29,29,30,0.12)] transition-all"
            />
            <button
              type="submit"
              onTouchEnd={(e) => { e.preventDefault(); handleSubmit(e as any); }}
              disabled={isPending || editPending || !text.trim()}
              className={`w-[40px] h-[40px] flex items-center justify-center rounded-full text-white disabled:opacity-40 transition-all flex-shrink-0 ${
                editingMessage ? 'bg-[#0ea5c9] hover:bg-[#0B8AAD]' : 'bg-[#1D1D1E] hover:bg-[#2E2E30]'
              }`}
            >
              {editingMessage ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.315.036a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
                </svg>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export const ChatToggleButton = ({
  onClick,
  unreadCount
}: {
  onClick: () => void;
  unreadCount?: number;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="fixed bottom-[24px] right-[24px] w-[56px] h-[56px] rounded-full bg-[#1D1D1E] text-white shadow-[0_4px_20px_rgba(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.3)] hover:scale-105 transition-all flex items-center justify-center z-30"
  >
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
    {unreadCount && unreadCount > 0 ? (
      <span className="absolute -top-[4px] -right-[4px] w-[20px] h-[20px] rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
        {unreadCount > 9 ? '9+' : unreadCount}
      </span>
    ) : null}
  </button>
);
